import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Calendar,
  Clock,
  MapPin,
  ArrowRight,
  User,
  Play,
  Radio,
} from "lucide-react";
import Reveal from "@/components/landing/Reveal";
import Seo from "@/components/Seo";
import { EVENT, MODERATOR, SPEAKERS, PARTNERS, COMMITTEE, SCHEDULE } from "@/data/eventData";

const initials = (name) =>
  name
    .replace(/(Dr\.|Prof\.|Mr\.|Ms\.|Mrs\.)/g, "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

const RegisterButton = ({ testid, label, className = "" }) => (
  <a
    href={EVENT.registerUrl}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className={`group inline-flex items-center gap-2 rounded-full bg-[#D91C29] text-white font-semibold px-7 py-3.5 shadow-sm hover:bg-[#b3141f] hover:-translate-y-0.5 transition-transform transition-colors duration-200 w-fit ${className}`}
  >
    {label}
    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
  </a>
);

const EventPage = () => {
  return (
    <div data-testid="event-page" className="bg-[#FAFAFA] min-h-screen">
      <Seo
        title="Terms of Trade: India, Britain & the Long Shadow of Empire | Urzu Media"
        description="Join the panel discussion 'Terms of Trade: India, Britain, and the Long Shadow of Empire' on July 17, 2026 at India Habitat Centre, New Delhi. An invite-only dialogue on the India-UK FTA and colonial economic history, hosted by Urzu Media."
      />
      {/* Banner (full-bleed, no side canvas) */}
      <section data-testid="event-banner" className="w-full">
        <motion.img
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
          src={EVENT.banner}
          alt="Terms of Trade: India, Britain, and the Long Shadow of Empire"
          data-testid="event-banner-image"
          className="w-full h-auto object-cover block"
        />
      </section>

      {/* Intro / core message */}
      <section className="py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-widest text-[#D91C29] font-semibold">{EVENT.kicker}</p>
            <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B1B3D] tracking-tight mt-3 leading-tight">
              {EVENT.title}
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-slate-700 font-medium leading-relaxed border-l-4 border-[#D91C29] pl-5">
              {EVENT.tagline}
            </p>
            <p className="mt-8 text-slate-600 leading-relaxed text-base sm:text-lg">
              {EVENT.description}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Details */}
      <section className="pb-4">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: Calendar, label: "Date", value: EVENT.date },
              { icon: Clock, label: "Time", value: EVENT.time },
              { icon: MapPin, label: "Venue", value: EVENT.venue, link: EVENT.mapUrl },
            ].map((d, i) => {
              const Icon = d.icon;
              return (
                <Reveal key={d.label} delay={i * 0.1}>
                  <div data-testid={`event-detail-${d.label.toLowerCase()}`} className="h-full rounded-2xl border border-slate-200 bg-white p-6">
                    <div className="w-11 h-11 rounded-xl bg-[#0B1B3D] flex items-center justify-center">
                      <Icon size={20} className="text-white" />
                    </div>
                    <p className="mt-4 text-xs uppercase tracking-widest text-slate-500">{d.label}</p>
                    <p className="mt-1.5 text-lg sm:text-xl font-bold text-[#0B1B3D] leading-snug">{d.value}</p>
                    {d.link && (
                      <a
                        href={d.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="event-map-link"
                        className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#D91C29] hover:underline"
                      >
                        View on Google Maps
                        <ArrowRight size={14} />
                      </a>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-8">
              <RegisterButton testid="event-details-rsvp" label="Register to reserve your seat" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Speakers */}
      <section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-widest text-[#D91C29] font-semibold">The Panel</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-bold text-[#0B1B3D] tracking-tight mt-3">Speakers</h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {SPEAKERS.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.08}>
                <div data-testid={`speaker-${i}`} className="group rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <div className={`relative aspect-[4/3] flex items-center justify-center overflow-hidden ${s.image ? "bg-slate-100" : "bg-gradient-to-br from-[#0B1B3D] to-[#1e3a70]"}`}>
                    {s.image ? (
                      <img src={s.image} alt={s.name} className="w-full h-full object-contain" />
                    ) : (
                      <>
                        <span className="font-serif-display text-5xl font-bold text-white/90">{initials(s.name)}</span>
                        <div className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-white/10 backdrop-blur flex items-center justify-center">
                          <User size={16} className="text-white" />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-serif-display text-lg font-bold text-[#0B1B3D]">{s.name}</h3>
                    <p className="mt-1 text-sm text-slate-600 leading-snug">{s.role}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Moderator */}
          <Reveal delay={0.1}>
            <div data-testid="moderator-card" className="mt-8 rounded-2xl border border-[#0B1B3D]/15 bg-[#0B1B3D] overflow-hidden">
              <div className="flex flex-col sm:flex-row items-center gap-6 p-6 sm:p-8">
                <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden ring-4 ring-[#D91C29] shrink-0">
                  <img src={MODERATOR.image} alt={MODERATOR.name} className="w-full h-full object-cover" />
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#D91C29] font-semibold">Moderated By</p>
                  <h3 className="font-serif-display text-2xl font-bold text-white mt-2">{MODERATOR.name}</h3>
                  <p className="mt-1 text-slate-300">{MODERATOR.role}</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Schedule */}
      <section className="py-16 sm:py-24 bg-[#0B1B3D]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-widest text-[#D91C29] font-semibold">Programme</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-bold text-white tracking-tight mt-3">Schedule & Agenda</h2>
          </Reveal>
          <div className="mt-12 space-y-3">
            {SCHEDULE.map((item, i) => (
              <Reveal key={i} delay={Math.min(i * 0.05, 0.4)}>
                <div data-testid={`schedule-${i}`} className="flex flex-col sm:flex-row sm:items-stretch gap-4 sm:gap-6 rounded-xl border border-white/10 bg-white/[0.07] p-5 sm:p-6 hover:bg-white/[0.12] transition-colors">
                  <div className="shrink-0 sm:w-32">
                    <span className="inline-flex items-center rounded-md bg-[#D91C29] text-white text-sm font-bold px-3 py-1.5 whitespace-nowrap">{item.time}</span>
                  </div>
                  <div className="hidden sm:block w-px bg-white/15 shrink-0" />
                  <div className="min-w-0">
                    <h3 className="font-semibold text-white text-base sm:text-lg leading-snug">{item.title}</h3>
                    {item.detail && <p className="mt-2 text-sm sm:text-[15px] text-slate-200 leading-relaxed">{item.detail}</p>}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Supporting Partners */}
      <section className="py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-bold text-[#0B1B3D] tracking-tight">Supporting Partners</h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 gap-7 max-w-3xl mx-auto">
            {PARTNERS.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.1}>
                <div data-testid={`partner-${i}`} className="rounded-2xl border border-slate-200 bg-white p-8 flex items-center justify-center min-h-[180px]">
                  <img src={p.logo} alt={p.name} className="max-h-24 max-w-[80%] w-auto object-contain" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Organising Committee */}
      <section className="pb-16 sm:pb-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <Reveal>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-bold text-[#0B1B3D] tracking-tight mt-3 text-center">Organising Committee</h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 gap-5">
            {COMMITTEE.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.08}>
                <div data-testid={`committee-${i}`} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5">
                  <div className="w-12 h-12 rounded-full bg-[#0B1B3D] flex items-center justify-center shrink-0">
                    <span className="text-white font-semibold text-sm">{initials(m.name)}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-[#0B1B3D]">{m.name}</p>
                    <p className="text-sm text-slate-600 leading-snug">{m.role}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Register CTA band */}
      <section id="rsvp" data-testid="rsvp-section" className="py-16 sm:py-20 bg-white border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-widest text-[#D91C29] font-semibold">Invite only · Prior registration required</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-bold text-[#0B1B3D] tracking-tight mt-3">
              Reserve your seat at the discussion
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Complete the short registration form and our team will confirm your invitation.
            </p>
            <div className="mt-8 flex justify-center">
              <RegisterButton testid="rsvp-register-button" label="RSVP to join the discussion" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* YouTube Live */}
      <section data-testid="youtube-live-section" className="py-16 sm:py-24 bg-[#0B1B3D]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Link to="/youtube-stream" data-testid="live-thumb-link" className="block relative rounded-2xl overflow-hidden shadow-2xl aspect-video group">
              <img src={EVENT.liveThumb} alt="Panel discussion live stream" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/20" />
              <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-md bg-[#D91C29] px-3 py-1.5 text-white text-xs font-bold tracking-wide">
                <Radio size={14} />
                LIVE
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-[#FF0000] flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
                  <Play size={30} className="text-white fill-white ml-1" />
                </div>
              </div>
              <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
                <p className="text-white text-sm font-medium">Watch on YouTube · Live 11:00 AM, 17 July 2026</p>
              </div>
            </Link>
          </Reveal>

          <Reveal delay={0.15}>
            <div>
              <p className="text-xs uppercase tracking-widest text-[#D91C29] font-semibold">Can&apos;t make it in person?</p>
              <h2 className="font-serif-display text-3xl sm:text-4xl font-bold text-white tracking-tight mt-3 leading-tight">
                Watch the panel live on YouTube
              </h2>
              <p className="mt-4 text-slate-300 leading-relaxed">
                The full discussion will be streamed live, free for everyone. The broadcast goes
                live at 11:00 AM IST on July 17, 2026.
              </p>
              <div className="mt-8">
                <Link
                  to="/youtube-stream"
                  data-testid="watch-live-cta"
                  className="group inline-flex items-center gap-2 rounded-full bg-[#D91C29] text-white font-semibold px-7 py-3.5 shadow-sm hover:bg-[#b3141f] hover:-translate-y-0.5 transition-transform transition-colors duration-200 w-fit"
                >
                  <Play size={18} className="fill-white" />
                  Watch the Live Stream
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Organised by */}
      <section className="py-14 bg-[#0a1733] text-center">
        <div className="max-w-3xl mx-auto px-5">
          <p className="text-xs uppercase tracking-widest text-slate-400">Organised by</p>
          <p className="mt-3 font-serif-display text-2xl sm:text-3xl font-bold text-white">Urzu Media Solutions</p>
        </div>
      </section>
    </div>
  );
};

export default EventPage;
